/**
 * Bootstrap API — one-shot fetch that hydrates the homepage's
 * first-paint chrome (account menu, sidebar nav, pins, persisted
 * UI preferences) on cold load.
 *
 * Replaces the parallel REST calls that used to fire on login:
 *   /v1/account/me
 *   /v1/account/preferences/map
 *   /v1/collection/list (×2 — both filtered and unfiltered)
 *   /v1/pin?scopeType=account&surface=feed
 *
 * Blocks are NOT in this payload. The homepage's first-paint cards
 * fire `/v1/block/list?limit=30` in parallel via
 * `initial-blocks-api.ts`'s `getInitialBlocksPromise()` singleton.
 * Splitting them out means cards paint the moment the block call
 * resolves — independent of bootstrap's slower fan-out
 * (collection-counts + per-scope ACL on pins). See that file's
 * header for the full rationale.
 *
 * Notifications used to ride along too but moved back out. The bell
 * popup runs a `refetchInterval`-driven query that handles cold-load
 * on its own, and the bell isn't above the fold — bundling
 * notifications into bootstrap cost the user two extra DB queries on
 * their slowest network hop for data the popup didn't actually
 * consume from bootstrap anyway.
 *
 * The server-side route at `/v1/bootstrap` runs the remaining entity
 * calls in parallel inside the worker, where intra-DC latency to
 * Hyperdrive is much faster than the round-trip from the browser.
 * See `production/api/src/api/bootstrap/routes.ts`.
 *
 * Why a singleton promise:
 *
 *   `ApiSetup` (top of the React tree, runs after login) needs the
 *   bootstrap payload to populate the pin store and preferences.
 *   But the `collectionCollection` TanStack DB queryFn ALSO needs
 *   this data — and it fires as soon as its first subscriber mounts,
 *   which happens BEFORE `ApiSetup`'s `useEffect` runs (children's
 *   effects fire before parents'). Without dedupe, we'd send two
 *   `/v1/bootstrap` requests per page load.
 *
 *   `useAccountMe` joins this same singleton (so `<AccountMenu>` and
 *   the author-filter UI never fire a separate `/v1/account/me`),
 *   and `useListGroups` filters `collectionCollection` directly
 *   instead of running its own `/v1/collection/list?layerIndex=0`.
 *   Cold load is two round-trips end-to-end (this + initial blocks).
 *
 *   `getBootstrapPromise()` returns the same in-flight promise on
 *   every call, regardless of caller. The first call kicks off the
 *   network request; every subsequent call within the same auth
 *   session joins the result. On logout `resetBootstrap()` clears
 *   the cache so the next login fires a fresh fetch.
 *
 * The granular endpoints (listBlocks, listCollections, fetchPins,
 * fetchAccountMe, fetchAccountPreferencesMap, fetchNotifications)
 * stay live — they're the right path for refresh, pagination, focus
 * revalidation, and type-filtered queries that bootstrap doesn't
 * cover.
 */
import { apiGet, waitForAuthTokenProvider } from "./base-query-api";
// ---------------------------------------------------------------------------
// Singleton promise
// ---------------------------------------------------------------------------
let inflight;
/**
 * Return the in-flight (or completed) bootstrap promise for the
 * current session. First call kicks off the fetch; subsequent calls
 * join the same promise.
 *
 * On rejection the cached promise is cleared so the next caller
 * starts a fresh fetch. This keeps a transient network blip from
 * permanently broken the session — every observer (the collections'
 * `queryFn`s, `ApiSetup`'s hydrator) will see the rejection, but the
 * next mount/retry gets a fresh shot.
 */
export function getBootstrapPromise() {
    if (inflight)
        return inflight;
    inflight = doFetchBootstrap().catch((e) => {
        inflight = undefined;
        throw e;
    });
    return inflight;
}
/**
 * Clear the cached bootstrap promise. Called on logout so the next
 * login doesn't see the previous user's data.
 */
export function resetBootstrap() {
    inflight = undefined;
}
// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------
async function doFetchBootstrap() {
    // The first caller of `getBootstrapPromise()` is typically a
    // collection `queryFn` that fires the moment its first React
    // subscriber mounts — which happens BEFORE `<ApiSetup>`'s
    // `useEffect` has run `setAuthTokenProvider(getAccessToken)`.
    // Without this wait the fetch lands with no `Authorization`
    // header and the server returns 401. Pausing here costs nothing
    // once auth is wired (the helper resolves synchronously) and
    // closes the cold-start race.
    await waitForAuthTokenProvider();
    const res = await apiGet("/bootstrap");
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to bootstrap");
    }
    return res.data;
}
